import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Square,
  Settings2,
  ChevronDown,
  Layers,
  Type,
  RotateCcw,
  BookOpen,
} from "lucide-react";
import CompareReciters from "@/components/CompareReciters";

/**
 * Sticky bottom audio player bar with collapsible settings panel.
 * Auto-hides on idle (controlled by `playerVisible`).
 *
 * Props are grouped into bundles to keep the surface area sane:
 *   - playback   : { isPlaying, isStopped, currentLoop, currentSetLoop }
 *   - range      : { rangeEnabled, fromAyah, toAyah, totalSurahAyahs, ...setters }
 *   - repeats    : { repeatEachVerse, setRepeatEachVerse, repeatSet, setRepeatSet }
 *   - modes      : { reviewMode, stackMode, stackLevel, stackCurrentIndex, currentAyahIndex, displayAyahs, ...setters }
 *   - prefs      : { fontSize, setFontSize, readingWidth, setReadingWidth, showTransliteration, setShowTransliteration, showTranslation, setShowTranslation, showWordByWord, setShowWordByWord, settings, setSettings }
 *   - actions    : { togglePlayPause, stopAudio, goToPrevAyah, goToNextAyah }
 */
export default function AudioControls({
  playerVisible,
  showSettings,
  setShowSettings,
  surahNumber,
  currentReciter,
  onSetDefaultReciter,
  playback,
  range,
  repeats,
  modes,
  prefs,
  actions,
}) {
  const { isPlaying, isStopped, currentLoop, currentSetLoop } = playback;
  const {
    rangeEnabled,
    setRangeEnabled,
    fromAyah,
    setFromAyah,
    toAyah,
    setToAyah,
    totalSurahAyahs,
    surahRangeEnabled,
    setSurahRangeEnabled,
    fromSurah,
    setFromSurah,
    toSurah,
    setToSurah,
    surahList = [],
  } = range;
  const { repeatEachVerse, setRepeatEachVerse, repeatSet, setRepeatSet } = repeats;
  const {
    reviewMode,
    setReviewMode,
    stackMode,
    setStackMode,
    setStackLevel,
    setStackCurrentIndex,
    setCurrentLoop,
    stackLevel,
    currentAyahIndex,
    displayAyahs,
  } = modes;
  const {
    fontSize,
    setFontSize,
    readingWidth,
    setReadingWidth,
    showTransliteration,
    setShowTransliteration,
    showTranslation,
    setShowTranslation,
    showWordByWord,
    setShowWordByWord,
    showTafsir,
    setShowTafsir,
    tafsirEdition,
    setTafsirEdition,
    continuousPageTurn,
    setContinuousPageTurn,
    continuousPageSeconds,
    setContinuousPageSeconds,
    viewMode,
    settings = {},
    setSettings,
  } = prefs;
  const { togglePlayPause, stopAudio, goToPrevAyah, goToNextAyah } = actions;

  return (
    <div
      data-testid="audio-player-bar"
      className={`fixed bottom-12 left-0 right-0 glass border-t border-white/5 transition-all duration-300 ease-out ${
        playerVisible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-full pointer-events-none"
      }`}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
        {showSettings && (
          <div className="mb-3 p-3 bg-islamic-blue-900/50 rounded-xl space-y-3 text-sm max-h-[50vh] overflow-y-auto">
            {/* Unified Recitation Range — full surah:ayah granularity on both ends */}
            <div className="border-b border-white/10 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300 font-outfit font-medium">Recitation Range</span>
                <Switch
                  data-testid="range-toggle"
                  checked={rangeEnabled}
                  onCheckedChange={(v) => {
                    setRangeEnabled(v);
                    if (v) {
                      // Sensible default: restrict to the current surah only.
                      const cur = parseInt(surahNumber) || 1;
                      setFromSurah(cur);
                      setToSurah(cur);
                      setFromAyah(1);
                      setToAyah(null);
                    }
                  }}
                />
              </div>
              {rangeEnabled && (() => {
                const fromSurahMeta = surahList.find((s) => s.number === fromSurah);
                const toSurahMeta = surahList.find((s) => s.number === toSurah);
                const fromAyahCount = fromSurahMeta?.ayahs ?? 7;
                const toAyahCount = toSurahMeta?.ayahs ?? 7;
                return (
                  <div className="space-y-2">
                    {/* FROM row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-500 text-xs w-8">From:</span>
                      <Select
                        value={String(fromSurah)}
                        onValueChange={(v) => {
                          const n = parseInt(v);
                          setFromSurah(n);
                          setFromAyah(1);
                          if (n > toSurah) {
                            setToSurah(n);
                            setToAyah(null);
                          }
                        }}
                      >
                        <SelectTrigger
                          data-testid="range-from-surah"
                          className="w-36 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-64">
                          {surahList.map((s) => (
                            <SelectItem
                              key={s.number}
                              value={String(s.number)}
                              className="text-slate-200 text-xs"
                            >
                              {s.number}. {s.englishName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-slate-500 text-xs">:</span>
                      <Select
                        value={String(fromAyah)}
                        onValueChange={(v) => setFromAyah(parseInt(v))}
                      >
                        <SelectTrigger
                          data-testid="range-from-ayah"
                          className="w-20 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-64">
                          {Array.from({ length: fromAyahCount }, (_, i) => i + 1).map((n) => (
                            <SelectItem key={n} value={String(n)} className="text-slate-200 text-xs">
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* TO row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-500 text-xs w-8">To:</span>
                      <Select
                        value={String(toSurah)}
                        onValueChange={(v) => {
                          const n = parseInt(v);
                          setToSurah(n);
                          setToAyah(null);
                        }}
                      >
                        <SelectTrigger
                          data-testid="range-to-surah"
                          className="w-36 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-64">
                          {surahList
                            .filter((s) => s.number >= fromSurah)
                            .map((s) => (
                              <SelectItem
                                key={s.number}
                                value={String(s.number)}
                                className="text-slate-200 text-xs"
                              >
                                {s.number}. {s.englishName}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <span className="text-slate-500 text-xs">:</span>
                      <Select
                        value={String(toAyah ?? toAyahCount)}
                        onValueChange={(v) => setToAyah(parseInt(v))}
                      >
                        <SelectTrigger
                          data-testid="range-to-ayah"
                          className="w-20 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-64">
                          {Array.from({ length: toAyahCount }, (_, i) => i + 1)
                            .filter((n) => fromSurah !== toSurah || n >= fromAyah)
                            .map((n) => (
                              <SelectItem key={n} value={String(n)} className="text-slate-200 text-xs">
                                {n}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-gold-400/80 text-xs pt-1">
                      {fromSurah === toSurah
                        ? `Verses ${fromAyah}–${toAyah ?? toAyahCount} of ${fromSurahMeta?.englishName ?? "this surah"}`
                        : `${fromSurahMeta?.englishName ?? fromSurah}:${fromAyah} → ${toSurahMeta?.englishName ?? toSurah}:${toAyah ?? toAyahCount} (${toSurah - fromSurah + 1} surahs)`}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Repeat Each Verse */}
            <div className="flex items-center justify-between">
              <span className="text-slate-300 font-outfit">Repeat Each Verse</span>
              <div className="flex items-center gap-2">
                <Select
                  value={String(repeatEachVerse)}
                  onValueChange={(v) => setRepeatEachVerse(parseInt(v))}
                >
                  <SelectTrigger className="w-20 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-48">
                    {[...Array.from({ length: 20 }, (_, i) => i + 1), -1].map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-slate-200 text-xs">
                        {n === -1 ? "∞" : `${n}x`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Repeat Set */}
            <div className="flex items-center justify-between">
              <span className="text-slate-300 font-outfit">Repeat Set</span>
              <div className="flex items-center gap-2">
                <Select value={String(repeatSet)} onValueChange={(v) => setRepeatSet(parseInt(v))}>
                  <SelectTrigger className="w-20 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-48">
                    {[...Array.from({ length: 20 }, (_, i) => i + 1), -1].map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-slate-200 text-xs">
                        {n === -1 ? "∞" : `${n}x`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-white/10 pt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-purple-400" />
                  <span className="text-slate-300 font-outfit">Review Mode</span>
                </div>
                <Switch checked={reviewMode} onCheckedChange={setReviewMode} />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-400" />
                <span className="text-slate-300 font-outfit">Stack Mode (1, 1-2, 1-2-3...)</span>
              </div>
              <Switch
                checked={stackMode}
                onCheckedChange={(v) => {
                  setStackMode(v);
                  setStackLevel(1);
                  setStackCurrentIndex(0);
                  setCurrentLoop(0);
                }}
              />
            </div>
            {stackMode && (
              <div className="bg-emerald-500/10 p-2 rounded-lg flex justify-between">
                <span className="text-emerald-400 font-outfit">
                  Current: Ayahs 1-{stackLevel}
                </span>
                <span className="text-emerald-400 font-outfit">
                  Loop {currentLoop + 1}/{repeatEachVerse === -1 ? "∞" : repeatEachVerse}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-slate-300 font-outfit">Pause Between</span>
              <div className="flex items-center gap-2">
                <Slider
                  value={[settings.pauseDuration ?? 0]}
                  onValueChange={([v]) => setSettings((s) => ({ ...s, pauseDuration: v }))}
                  min={0}
                  max={10}
                  step={0.5}
                  className="w-24"
                />
                <span className="text-gold-400 font-outfit w-6">{settings.pauseDuration ?? 0}s</span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Type className="w-4 h-4 text-gold-400" />
                  <span className="text-slate-300 font-outfit">Font Size</span>
                </div>
                <Select value={fontSize} onValueChange={setFontSize}>
                  <SelectTrigger
                    data-testid="font-size-select"
                    className="w-32 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-islamic-blue-900 border-white/10">
                    <SelectItem value="small" className="text-slate-200 text-xs">
                      Small
                    </SelectItem>
                    <SelectItem value="medium" className="text-slate-200 text-xs">
                      Medium
                    </SelectItem>
                    <SelectItem value="large" className="text-slate-200 text-xs">
                      Large
                    </SelectItem>
                    <SelectItem value="xlarge" className="text-slate-200 text-xs">
                      Extra Large
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gold-400" />
                  <span className="text-slate-300 font-outfit">Reading Width</span>
                </div>
                <Select value={readingWidth} onValueChange={setReadingWidth}>
                  <SelectTrigger
                    data-testid="reading-width-select"
                    className="w-32 h-7 bg-islamic-blue-800 border-white/10 text-slate-200 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-islamic-blue-900 border-white/10">
                    <SelectItem value="compact" className="text-slate-200 text-xs">
                      Compact
                    </SelectItem>
                    <SelectItem value="comfortable" className="text-slate-200 text-xs">
                      Comfortable
                    </SelectItem>
                    <SelectItem value="wide" className="text-slate-200 text-xs">
                      Wide
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Type className="w-4 h-4 text-gold-400" />
                  <span className="text-slate-300 font-outfit">Transliteration</span>
                </div>
                <Switch checked={showTransliteration} onCheckedChange={setShowTransliteration} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 font-outfit">Translation</span>
                <Switch checked={showTranslation} onCheckedChange={setShowTranslation} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 font-outfit" data-testid="wbw-toggle-label">
                  Word-by-word
                </span>
                <Switch
                  data-testid="wbw-toggle"
                  checked={showWordByWord}
                  onCheckedChange={setShowWordByWord}
                />
              </div>
              <div className="pt-1">
                <CompareReciters
                  surahNum={parseInt(surahNumber) || 1}
                  ayahNum={
                    displayAyahs?.[currentAyahIndex]?.numberInSurah ?? 1
                  }
                  currentReciter={currentReciter}
                  onSetDefault={onSetDefaultReciter}
                />
              </div>
              {viewMode === "page" && (
                <>
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    <span
                      className="text-slate-300 font-outfit"
                      data-testid="continuous-page-toggle-label"
                    >
                      Continuous page-turn
                    </span>
                    <Switch
                      data-testid="continuous-page-toggle"
                      checked={!!continuousPageTurn}
                      onCheckedChange={setContinuousPageTurn}
                    />
                  </div>
                  {continuousPageTurn && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-slate-400 font-outfit text-sm">
                        Turn every
                      </span>
                      <Select
                        value={String(continuousPageSeconds || 30)}
                        onValueChange={(v) =>
                          setContinuousPageSeconds(parseInt(v))
                        }
                      >
                        <SelectTrigger
                          data-testid="continuous-page-seconds"
                          className="w-[140px] bg-islamic-blue-800 border-white/10 text-slate-200"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-islamic-blue-900 border-white/10 text-slate-200">
                          <SelectItem value="15">15 seconds</SelectItem>
                          <SelectItem value="30">30 seconds</SelectItem>
                          <SelectItem value="45">45 seconds</SelectItem>
                          <SelectItem value="60">1 minute</SelectItem>
                          <SelectItem value="90">1.5 minutes</SelectItem>
                          <SelectItem value="120">2 minutes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              {viewMode === "flashcard" && (
                <>
                  <div className="flex items-center justify-between">
                    <span
                      className="text-slate-300 font-outfit"
                      data-testid="tafsir-toggle-label"
                    >
                      Tafsir (commentary)
                    </span>
                    <Switch
                      data-testid="tafsir-toggle"
                      checked={!!showTafsir}
                      onCheckedChange={setShowTafsir}
                    />
                  </div>
                  {showTafsir && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-slate-400 font-outfit text-sm">Edition</span>
                      <Select
                        value={tafsirEdition || "en.maududi"}
                        onValueChange={setTafsirEdition}
                      >
                        <SelectTrigger
                          data-testid="tafsir-edition-select"
                          className="w-[200px] bg-islamic-blue-800 border-white/10 text-slate-200"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-islamic-blue-900 border-white/10 text-slate-200">
                          <SelectItem value="en.maududi">Tafhim al-Quran (EN)</SelectItem>
                          <SelectItem value="ar.muyassar">Al-Muyassar (AR)</SelectItem>
                          <SelectItem value="ar.jalalayn">Al-Jalalayn (AR)</SelectItem>
                          <SelectItem value="ar.qurtubi">Al-Qurtubi (AR)</SelectItem>
                          <SelectItem value="ar.miftah">Miftah al-Quran (AR)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Playback Status Bar */}
        {isPlaying && (
          <div className="mb-2 px-2 py-1 bg-gold-400/10 rounded-lg flex items-center justify-between text-xs">
            <span className="text-gold-400 font-outfit">
              {stackMode
                ? `Stack ${stackLevel} • Ayah ${currentAyahIndex + 1}`
                : `Ayah ${displayAyahs[currentAyahIndex]?.numberInSurah || 1}`}
            </span>
            <span className="text-slate-400 font-outfit">
              Verse {currentLoop + 1}/{repeatEachVerse === -1 ? "∞" : repeatEachVerse}
              {repeatSet > 1 &&
                ` • Set ${currentSetLoop + 1}/${repeatSet === -1 ? "∞" : repeatSet}`}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {repeatEachVerse > 1 && (
              <span className="text-xs text-gold-400 bg-gold-400/10 px-2 py-0.5 rounded">
                {currentLoop + 1}/{repeatEachVerse === -1 ? "∞" : repeatEachVerse}
              </span>
            )}
            {repeatSet > 1 && (
              <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">
                Set {currentSetLoop + 1}/{repeatSet === -1 ? "∞" : repeatSet}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              data-testid="audio-prev-btn"
              variant="ghost"
              size="icon"
              onClick={goToPrevAyah}
              className="text-slate-400 hover:text-gold-400"
            >
              <SkipBack className="w-5 h-5" />
            </Button>
            <Button
              data-testid="audio-stop-btn"
              variant="ghost"
              size="icon"
              onClick={stopAudio}
              className={`hover:text-red-400 ${isStopped ? "text-red-400" : "text-slate-400"}`}
              title="Stop - Click any ayah to play from there"
            >
              <Square className="w-5 h-5" />
            </Button>
            <Button
              data-testid="audio-play-btn"
              onClick={togglePlayPause}
              className="w-12 h-12 rounded-full btn-gold"
              title={isStopped ? "Play from beginning" : isPlaying ? "Pause" : "Resume"}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </Button>
            <Button
              data-testid="audio-next-btn"
              variant="ghost"
              size="icon"
              onClick={goToNextAyah}
              className="text-slate-400 hover:text-gold-400"
            >
              <SkipForward className="w-5 h-5" />
            </Button>
            {/* Compact playback-speed picker — quick-access beside the
                forward button so the user doesn't have to open the settings
                panel to slow audio down for memorization practice. */}
            <Select
              value={String(settings.playbackSpeed ?? 1)}
              onValueChange={(v) =>
                setSettings((s) => ({ ...s, playbackSpeed: parseFloat(v) }))
              }
            >
              <SelectTrigger
                data-testid="playback-speed-inline"
                className="h-8 w-[58px] px-2 bg-transparent border-white/15 text-slate-300 hover:text-gold-400 hover:border-gold-400/40 text-xs font-outfit"
                title="Playback speed"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-islamic-blue-900 border-white/10 min-w-[72px]">
                <SelectItem value="0.5" className="text-slate-200 text-xs">0.5x</SelectItem>
                <SelectItem value="0.75" className="text-slate-200 text-xs">0.75x</SelectItem>
                <SelectItem value="1" className="text-slate-200 text-xs">1x</SelectItem>
                <SelectItem value="1.25" className="text-slate-200 text-xs">1.25x</SelectItem>
                <SelectItem value="1.5" className="text-slate-200 text-xs">1.5x</SelectItem>
                <SelectItem value="1.75" className="text-slate-200 text-xs">1.75x</SelectItem>
                <SelectItem value="2" className="text-slate-200 text-xs">2x</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            data-testid="audio-settings-btn"
            variant="ghost"
            size="icon"
            onClick={() => setShowSettings(!showSettings)}
            className="text-slate-400 hover:text-gold-400"
          >
            {showSettings ? <ChevronDown className="w-5 h-5" /> : <Settings2 className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
